import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Snackbar, Alert, Chip, Tabs, Tab, Grid
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon,
  Person as PersonIcon, Security as SecurityIcon
} from '@mui/icons-material';
import api from '../api/api';

const FacilityAccessControl = () => {
  const [profiles, setProfiles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeProfiles, setEmployeeProfiles] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [openAssignmentDialog, setOpenAssignmentDialog] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [newAssignment, setNewAssignment] = useState({
    FacilityAccessProfileId: '',
    StartDate: '',
    EndDate: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await api.get('/facility-access-profiles');
      setProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      showSnackbar('Profiller yüklenirken hata oluştu', 'error');
    }
  }, [showSnackbar]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      showSnackbar('Personeller yüklenirken hata oluştu', 'error');
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchProfiles();
    fetchEmployees();
  }, [fetchProfiles, fetchEmployees]);

  const fetchEmployeeProfiles = async (employeeId) => {
    try {
      const response = await api.get(`/employees/${employeeId}/facility-profiles`);
      setEmployeeProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Error fetching employee profiles:', error);
      showSnackbar('Personel profilleri yüklenirken hata oluştu', 'error');
    }
  };

  const handleOpenProfileDialog = (profile = null) => {
    setEditingProfile(profile || {
      ProfileName: '',
      Description: ''
    });
    setOpenProfileDialog(true);
  };

  const handleOpenAssignmentDialog = (employee) => {
    setSelectedEmployee(employee);
    setNewAssignment({
      FacilityAccessProfileId: '',
      StartDate: '',
      EndDate: ''
    });
    setOpenAssignmentDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenProfileDialog(false);
    setOpenAssignmentDialog(false);
    setEditingProfile(null);
    setNewAssignment({ FacilityAccessProfileId: '', StartDate: '', EndDate: '' });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProfile.ID) {
        // Güncelleme - PUT
        await api.put(`/facility-access-profiles/${editingProfile.ID}`, editingProfile);
        showSnackbar('Profil güncellendi', 'success');
      } else {
        // Yeni oluşturma - POST
        await api.post('/facility-access-profiles', editingProfile);
        showSnackbar('Profil oluşturuldu', 'success');
      }
      fetchProfiles();
      handleCloseDialogs();
    } catch (error) {
      console.error('Error saving profile:', error);
      showSnackbar('Profil kaydedilirken hata oluştu', 'error');
    }
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/employees/${selectedEmployee.EmployeeId}/facility-profiles`, newAssignment);
      showSnackbar('Profil ataması yapıldı', 'success');
      if (selectedEmployee) {
        fetchEmployeeProfiles(selectedEmployee.EmployeeId);
      }
      handleCloseDialogs();
    } catch (error) {
      console.error('Error saving assignment:', error);
      showSnackbar('Profil ataması yapılırken hata oluştu', 'error');
    }
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    fetchEmployeeProfiles(employee.EmployeeId);
    setActiveTab(1);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getProfileColor = (profileName) => {
    const colors = {
      'FULL_ACCESS': 'success',
      'MANAGEMENT_ACCESS': 'warning',
      'STANDARD_ACCESS': 'info',
      'CAFETERIA_ONLY': 'primary'
    };
    return colors[profileName] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Tesis Erişim Kontrolü</Typography>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Erişim Profilleri" icon={<SecurityIcon />} />
        <Tab label="Personel Atamaları" icon={<PersonIcon />} />
      </Tabs>

      {/* Profiles Tab */}
      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Erişim Profilleri</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenProfileDialog()}
            >
              Yeni Profil Ekle
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Profil Adı</TableCell>
                  <TableCell>Açıklama</TableCell>
                  <TableCell>Oluşturulma Tarihi</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((profile) => (
                  <TableRow key={profile.ID}>
                    <TableCell>
                      <Chip 
                        label={profile.ProfileName} 
                        color={getProfileColor(profile.ProfileName)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{profile.Description || '-'}</TableCell>
                    <TableCell>{profile.CreatedAt ? new Date(profile.CreatedAt).toLocaleDateString('tr-TR') : '-'}</TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={() => handleOpenProfileDialog(profile)} 
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Employee Assignments Tab */}
      {activeTab === 1 && (
        <Box>
          <Grid container spacing={3}>
            {/* Employee List */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Personeller</Typography>
              <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Personel ID</TableCell>
                      <TableCell>Ad Soyad</TableCell>
                      <TableCell>Departman</TableCell>
                      <TableCell>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employees.map((employee) => (
                      <TableRow 
                        key={employee.EmployeeId}
                        selected={selectedEmployee?.EmployeeId === employee.EmployeeId}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEmployeeSelect(employee)}
                      >
                        <TableCell>{employee.EmployeeId}</TableCell>
                        <TableCell>{`${employee.FirstName} ${employee.LastName}`}</TableCell>
                        <TableCell>{employee.Department || '-'}</TableCell>
                        <TableCell>
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenAssignmentDialog(employee);
                            }}
                            color="primary"
                            size="small"
                          >
                            <AddIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            {/* Selected Employee Profiles */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {selectedEmployee ? `${selectedEmployee.FirstName} ${selectedEmployee.LastName} - Tesis Profilleri` : 'Personel Seçiniz'}
              </Typography>
              
              {selectedEmployee && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Mevcut Profiller
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenAssignmentDialog(selectedEmployee)}
                    >
                      Profil Ekle
                    </Button>
                  </Box>

                  <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Profil Adı</TableCell>
                          <TableCell>Başlangıç</TableCell>
                          <TableCell>Bitiş</TableCell>
                          <TableCell>Durum</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {employeeProfiles.map((profile) => (
                          <TableRow key={profile.ID}>
                            <TableCell>
                              <Chip 
                                label={profile.ProfileName} 
                                color={getProfileColor(profile.ProfileName)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{profile.StartDate}</TableCell>
                            <TableCell>{profile.EndDate || 'Süresiz'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={profile.IsActive ? 'Aktif' : 'Pasif'}
                                color={profile.IsActive ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                        {employeeProfiles.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                              <Typography variant="body2" color="textSecondary">
                                Bu personelin tesis profili bulunmamaktadır
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              )}

              {!selectedEmployee && (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Personel seçmek için listeden bir personel tıklayın
                  </Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Profile Dialog */}
      <Dialog open={openProfileDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <form onSubmit={handleProfileSubmit}>
          <DialogTitle>
            {editingProfile?.ID ? 'Profil Düzenle' : 'Yeni Erişim Profili Ekle'}
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              fullWidth
              label="Profil Adı"
              name="ProfileName"
              value={editingProfile?.ProfileName || ''}
              onChange={(e) => setEditingProfile(prev => ({ ...prev, ProfileName: e.target.value }))}
              required
            />
            <TextField
              margin="normal"
              fullWidth
              multiline
              rows={3}
              label="Açıklama"
              name="Description"
              value={editingProfile?.Description || ''}
              onChange={(e) => setEditingProfile(prev => ({ ...prev, Description: e.target.value }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>İptal</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingProfile?.ID ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={openAssignmentDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <form onSubmit={handleAssignmentSubmit}>
          <DialogTitle>Personel Profil Ataması</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              {selectedEmployee && `${selectedEmployee.FirstName} ${selectedEmployee.LastName} için profil seçin`}
            </Typography>
            
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Erişim Profili</InputLabel>
              <Select
                value={newAssignment.FacilityAccessProfileId}
                onChange={(e) => setNewAssignment(prev => ({ 
                  ...prev, 
                  FacilityAccessProfileId: e.target.value 
                }))}
              >
                {profiles.map((profile) => (
                  <MenuItem key={profile.ID} value={profile.ID}>
                    {profile.ProfileName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              margin="normal"
              fullWidth
              type="date"
              label="Başlangıç Tarihi"
              name="StartDate"
              value={newAssignment.StartDate}
              onChange={(e) => setNewAssignment(prev => ({ ...prev, StartDate: e.target.value }))}
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              margin="normal"
              fullWidth
              type="date"
              label="Bitiş Tarihi (Opsiyonel)"
              name="EndDate"
              value={newAssignment.EndDate}
              onChange={(e) => setNewAssignment(prev => ({ ...prev, EndDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>İptal</Button>
            <Button type="submit" variant="contained" color="primary">
              Ata
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacilityAccessControl;
